import React from 'react';

/**
 * A row of mutually exclusive choices — group-by, date range, list/grid.
 *
 * This is the shape Sales and Management already use: a bordered tray holding
 * squared buttons. Management's is `rounded-xl border p-1 shadow-sm` around
 * `h-9 px-4 rounded-lg` items; SalesMain's tabs are `px-3 py-1.5 rounded-[8px]
 * border`. Both are rectangles with corners taken off.
 *
 * The newer screens made the same control a row of `rounded-full` pills. That
 * one difference is most of why Labour and HR read as a different product from
 * Sales — a pill and a rectangle are different enough that the eye files them
 * as different kinds of thing, even when the colour matches.
 *
 * So: rectangles. The only round-ended thing in the old screens is a badge,
 * and a badge is not a control.
 *
 *   <SegmentedControl
 *     value={groupBy}
 *     onChange={setGroupBy}
 *     options={[{ value: 'project', label: 'By site' }, …]}
 *   />
 *
 * `tone="neutral"` gives the dark-grey selection Labour Spend used for its
 * second, subordinate control, so two segmented rows side by side stay
 * legible instead of competing for the same brand green.
 */
const SegmentedControl = ({
  value,
  onChange,
  options = [],
  tone = 'brand',
  size = 'md',
  ariaLabel,
  className = ''
}) => {
  const selected = tone === 'neutral' ? 'bg-gray-900 text-white' : 'bg-brand-700 text-white';
  const item =
    size === 'sm' ? 'h-8 px-3 text-xs rounded-action' : 'h-9 px-4 text-[13px] rounded-control';

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={`inline-flex items-center gap-1 rounded-panel border border-hairline bg-surface p-1 shadow-card ${className}`}>
      {options.map((o) => {
        const isOn = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={isOn}
            onClick={() => onChange?.(o.value)}
            className={`inline-flex shrink-0 items-center justify-center gap-1.5 font-semibold transition-colors
              focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600
              ${item} ${isOn ? selected : 'text-gray-600 hover:bg-surface-muted'}`}>
            {o.icon}
            {o.label}
          </button>
        );
      })}
    </div>
  );
};

export default SegmentedControl;
